import { useEffect } from "react";
import { Link } from "react-router-dom";
import { SparklesIcon, CheckCircleIcon, ShieldCheckIcon, HeartIcon, UserGroupIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import logo from "../Signup/img/logo.svg";
import '../Signup/landing.scss';

export default function Services() {
    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.classList.add("tailwind-html");

        return () => {
            htmlElement.classList.remove("tailwind-html");
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    const services = [
        {
            title: "In-Home Psychotherapy",
            description: "Professional mental health services delivered directly to your home",
            icon: CheckCircleIcon,
            details: [
                "Licensed therapists",
                "Convenient home visits",
                "Personalized care",
                "Medicare coverage"
            ]
        },
        {
            title: "Assessments",
            description: "Comprehensive evaluations to understand your needs",
            icon: CheckCircleIcon,
            details: [
                "Initial evaluation",
                "Treatment planning",
                "Progress tracking",
                "Medicare covered"
            ]
        },
        {
            title: "Individual & Family Therapy",
            description: "One-on-one and family counseling sessions",
            icon: CheckCircleIcon,
            details: [
                "Individual sessions",
                "Family therapy",
                "Personalized approach",
                "Medicare eligible"
            ]
        },
    ];

    return (
        <div className="landing-page">
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 rounded-3xl md:m-5">
                {/* Header */}
                <header className="relative z-40">
                    <nav
                        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
                        aria-label="Global"
                    >
                        <div className="flex lg:flex-1">
                            <Link to="/" className="flex items-center space-x-2">
                                <img
                                    src={logo}
                                    alt="Emissary Health"
                                    className="h-10 w-auto"
                                />
                            </Link>
                        </div>
                    </nav>
                </header>

                {/* Main Content */}
                <main className="relative isolate">
                    {/* Background Grid Pattern */}
                    <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-50" />

                    <div
                        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
                        aria-hidden="true"
                    >
                        <div
                            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-sky-300 to-sky-600 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                            style={{
                                clipPath:
                                    "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
                            }}
                        />
                    </div>

                    {/* Hero Section */}
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 sm:py-24">
                        <div className="mx-auto max-w-2xl">
                            <div className="mb-8 flex justify-center">
                                <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                    <SparklesIcon className="inline-block h-4 w-4 mr-1" />
                                    Mental Health Care
                                </div>
                            </div>
                            <h1 className="text-4xl font-bold tracking-tight sm:text-6xl bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                                Professional Mental Health Care in Your Home
                            </h1>
                            <div className="mt-8 text-lg leading-8 text-gray-600 space-y-4">
                                <p>
                                    We provide in-home psychotherapy services covered under Medicare including assessments and individual or family therapy. Coverage and patient responsibility vary based on your plan.
                                </p>
                                <p className="text-sm text-gray-500 italic">
                                    This is not a guarantee of coverage or payment. Please confirm your benefits with Medicare or your plan administrator.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 sm:py-24">
                        <div className="mx-auto max-w-2xl mb-12">
                            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                                Our Care Services
                            </h2>
                            <p className="mt-4 text-lg leading-8 text-gray-600">
                                We offer a range of mental health services tailored to your needs, all provided in the comfort of your home.
                            </p>
                        </div>
                        <div className="mx-auto max-w-2xl sm:mt-12 lg:mt-16 lg:max-w-none">
                            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-12 lg:max-w-none lg:grid-cols-3">
                                {services.map((service) => (
                                    <div key={service.title} className="flex flex-col bg-white rounded-2xl p-6 shadow-lg ring-1 ring-gray-200 hover:shadow-xl transition-shadow duration-300">
                                        <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                            <service.icon className="h-5 w-5 flex-none text-sky-600" aria-hidden="true" />
                                            {service.title}
                                        </dt>
                                        <dd className="mt-3 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                            <p className="flex-auto">{service.description}</p>
                                            <ul className="mt-4 space-y-2">
                                                {service.details.map((detail, index) => (
                                                    <li key={index} className="flex gap-x-3 items-start">
                                                        <CheckCircleIcon className="h-5 w-5 flex-none text-sky-600 mt-0.5" />
                                                        <span>{detail}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 sm:py-24">
                        <div className="relative isolate overflow-hidden bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-16 text-center shadow-2xl sm:rounded-3xl sm:px-16">
                            <div className="absolute inset-0 -z-10 opacity-20">
                                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
                            </div>
                            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-sky-500/30 to-blue-600/30 blur-3xl"></div>
                            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-sky-500/20 to-blue-600/20 animate-pulse"></div>
                            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Ready to Begin Your Journey?
                            </h2>
                            <p className="mx-auto mt-4 max-w-xl text-lg leading-8 text-sky-100">
                                Contact us today to learn more about our services and check your Medicare coverage.
                            </p>
                            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-sky-100/90">
                                This is not a guarantee of coverage or payment. Please confirm your benefits with Medicare or your plan administrator.
                            </p>
                            <div className="mt-8 flex items-center justify-center gap-x-6">
                                <Link
                                    to="/check-eligibility"
                                    className="group relative rounded-md bg-white px-8 py-4 text-base font-semibold text-sky-600 shadow-sm hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all duration-300 hover:scale-105"
                                >
                                    <span className="relative z-10 flex items-center">
                                        Check Your Eligibility
                                        <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5 inline-block transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                    <div className="absolute inset-0 rounded-md bg-gradient-to-r from-white to-sky-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute inset-0 rounded-md bg-gradient-to-r from-white to-sky-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
} 